import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./OrdersView.css";
import { deleteDraftOrder, getOrders } from "../../services/orders.service";
import LeftNavigationBar from "../LeftNavigationBar/LeftNavigationBar";
import { Loader, Placeholder } from 'rsuite';
import OrderSvg from '../../assets/images/svgs/Order.svg?react';
import { formatFullDate } from "../../common/utils/helperFunctions";
import aoiImg from "../../assets/images/brand/aoi.png";
import GreenTickIcon from "../../assets/images/svgs/GreenTickIcon.svg?react";
import YellowPendingIcon from "../../assets/images/svgs/YellowPendingIcon.svg?react";
import GsdIcon from "../../assets/images/svgs/GsdIcon.svg?react";
import SensorIcon from "../../assets/images/svgs/SensorIcon.svg?react";
import ImageCountIcon from "../../assets/images/svgs/ImageCountIcon.svg?react";
import cloudPercentageImg from "../../assets/images/brand/cloud-percentage.png";
import orderDataTypeImg from "../../assets/images/brand/order-data-type.png";
import priorityAsapImg from "../../assets/images/brand/priority-asap.png";
import priorityHighImg from "../../assets/images/brand/priority-high.png";
import priorityNormalImg from "../../assets/images/brand/priority-normal.png";
import recurringImg from "../../assets/images/brand/recurring.png";
import MapView from "../MapView/MapView";
import { clearSingleOrder, getSingleOrdersSuccess, resetOrdersSlice, setEditOrder } from "../../slices/orderSlice";
import { resetMapSlice, resetOrderAoi, resetOrdersRoute, setDisplayView, setOrderAoi, setOrdersRoute } from "../../slices/mapSlice";
import { searchOrderItem } from "../../services/stac.service.jsx";
import { searchStacDataRequest, setSelectedFeature } from "../../slices/searchSlice";
import { Close, CompletedIcon, DustBinIcon, FilterIcon, InfoIcon, ListIcon, MapViewIcon, PendingIcon, TickIcon } from "../../assets/images/svgs/Icons";
import { Tooltip } from "react-tooltip";
import SingleSelectDropdown from "../../common/utils/SingleSelectDropdown";
import { equalityOptions, propertyOptions, providerTypeOptions, statusOptions } from "../../common/constants/orderOptions";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";

const OrdersView = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orders = useSelector(state => state.orderDetails.orders);
  const singleOrder = useSelector(state => state.orderDetails.singleOrder);
  const collectionsData = useSelector(state => state.collectionDetails.collections);
  const providersData = useSelector(state => state.providerDetails.providers);
  const loadingOrders = useSelector(state => state.orderDetails.loadingOrders);
  const editOrder = useSelector(state => state.orderDetails.editOrder);
  const [listView, setListView] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [filters, setFilters] = useState({
    field: null,
    operator: equalityOptions[0]?.propertyID,
    values: null,
  });

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showStatusLegend, setShowStatusLegend] = useState(false);
  const [orderDetailsCollapsed, setOrderDetailsCollapsed] = useState(false);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const [capturedDataVisible, setCapturedDataVisible] = useState(false);
  const [statusGroupFilter, setStatusGroupFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);

  const isFilterComplete =
    filters.field &&
    filters.operator &&
    filters.values;



  useEffect(() => {
    dispatch(setOrdersRoute());
    return () => {
      dispatch(resetOrdersRoute());
    };
  }, [dispatch]);

  useEffect(() => {
    if (filterList.length > 0) {
      dispatch(getOrders(filterList));
    } else {
      dispatch(getOrders([]));
    }

    return () => {
      if (!Object.keys(editOrder).length) {
        dispatch(resetMapSlice());
        dispatch(resetOrdersSlice());
      }
    }
  }, [dispatch, filterList, editOrder]);

  const getPrioritySrc = (item) => {
    if (item.antaris?.priorityLane === 'ASAP') {
      return priorityAsapImg;
    } else if (item.antaris?.priorityLane === 'High') {
      return priorityHighImg;
    } else {
      return priorityNormalImg;
    }
  }

  const handleToggleListView = () => {
    setListView(true);
    dispatch(resetMapSlice());
    dispatch(clearSingleOrder());
    setSelectedOrders([]); // Clear selections when switching views
    setCapturedDataVisible(false);
  };

  const handleToggleMapView = () => {
    setListView(false);
  }

  const getValueOptions = () => {
    if (filters.field === "status") return statusOptions;
    if (filters.field === "providertype") return providerTypeOptions;
    return []; // no options yet
  };

  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  }

  const handleDropdownChange = (value, name) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRemoveFilter = () => {
    setFilters({
      field: null,
      operator: equalityOptions[0]?.propertyID,
      values: null,
    });
    setOpenFilter(false);
  };

  const handleSubmitFilter = () => {
    if (!isFilterComplete) return;

    setFilterList(prev => {
      const normalized = {
        ...filters,
        values: Array.isArray(filters.values) ? filters.values : [filters.values],
      };

      const updated = [...prev, normalized];
      return updated;
    });


    setFilters({
      field: null,
      operator: equalityOptions[0]?.propertyID,
      values: null,
    });
    setOpenFilter(false);
  };

  const clearFilter = () => {
    setFilterList([]);
  }

  const handleEditOrder = (item, type) => {
    dispatch(setEditOrder(item));
    if (type === "Order Creation") {
      dispatch(setDisplayView("createOrderView"));
      navigate('/');
      // navigate('/createOrder');
    }
  }

  const handleDeleteOrder = (item) => {
    dispatch(deleteDraftOrder(item.providerTaskID, getNewOrdersList))
  }

  const getNewOrdersList = () => {
    dispatch(getOrders([]))
  }

  const statusIcon = {
    Pending: <PendingIcon />,
    Submitted: <YellowPendingIcon />,
    Active: <Loader inverse speed="paused" />,
    DataReady: <GreenTickIcon />,
    Success: <CompletedIcon />
  }

  const handleOrderClick = (item, event) => {
    const providerKey = item?.antaris ? 'antaris' : 'strataTest';
    const aoi = providerKey === 'antaris' ? item?.antaris?.aoi : item?.strataTest?.aoi;

    const enrichedAoi = {
      type: 'Feature',
      geometry: aoi,
      properties: {
        orderName: item.name ?? `Antaris-${item.providerID.split('-')[0]}`,
        status: item.status,
        provider: providerKey,
        priority: item.priority,
        providerTaskID: item.providerTaskID,
        createdAt: item.createdAt,
      },
    };

    if (event.ctrlKey || event.metaKey) {
      // Detail card is single-order only - hide it once more than one order is selected
      dispatch(clearSingleOrder());
      setCapturedDataVisible(false);
      setSelectedOrders(prev => {
        const isAlreadySelected = prev.some(
          order => order.providerTaskID === item.providerTaskID
        );

        if (isAlreadySelected) {
          // Remove from selection
          return prev.filter(order => order.providerTaskID !== item.providerTaskID);
        } else {
          // Add to selection
          return [...prev, { item, enrichedAoi }];
        }
      });
    } else {
      // NORMAL CLICK: Select only this order
      setSelectedOrders([{ item, enrichedAoi }]);
      setOrderDetailsCollapsed(false);
      setOrderDetailsExpanded(false);
      setCapturedDataVisible(false);
      dispatch(getSingleOrdersSuccess(item));
      dispatch(setOrderAoi(enrichedAoi));

      // Order click shows only the AOI - the stac items/footprints don't
      // fetch until the user toggles them on via the Captured Data pill, so
      // clear whatever the previous order left on the "stac" layer.
      dispatch(searchStacDataRequest());
    }
  };

  // Captured Data pill toggles the "stac" GeoJSON layer for the current
  // order: first click fetches the order's items (populating searchResults,
  // which the "stac" layer renders as footprints), second click hides them
  // again by clearing searchResults.
  const handleToggleCapturedData = () => {
    if (capturedDataVisible) {
      dispatch(searchStacDataRequest());
      setCapturedDataVisible(false);
      return;
    }

    if (!singleOrder?.items?.length) return;

    // Each id still dispatches its own independent searchOrderItem action
    // (same as before) - only wait here, at the orchestration level, until
    // every footprint has loaded before selecting the first one, so the
    // raster preview doesn't pop in mid-way through the footprints appearing.
    const fetches = singleOrder.items.map((itemID) =>
      dispatch(searchOrderItem(singleOrder.collectionID, itemID))
    );
    Promise.all(fetches).then((features) => {
      const firstFeature = features.find(Boolean);
      if (firstFeature) {
        dispatch(setSelectedFeature(firstFeature));
      }
    });

    setCapturedDataVisible(true);
  };

  useEffect(() => {
    if (selectedOrders.length > 0) {
      // Dispatch all selected orders - map will handle rendering multiple
      const allAois = selectedOrders.map(selected => selected.enrichedAoi);
      dispatch(setOrderAoi(allAois));

      // Set the last selected as the single order for detail view
      // const lastSelected = selectedOrders[selectedOrders.length - 1];
      // dispatch(getSingleOrdersSuccess(lastSelected.item));
    }
  }, [selectedOrders, dispatch]);

  // Drops the selection, AOI, captured-data footprints and the details card
  const deselectAllOrders = useCallback(() => {
    setSelectedOrders([]);
    setCapturedDataVisible(false);
    setOrderDetailsExpanded(false);
    dispatch(clearSingleOrder());
    dispatch(resetOrderAoi());
    dispatch(searchStacDataRequest());
  }, [dispatch]);

  // Esc deselects the current order(s)
  useEffect(() => {
    if (selectedOrders.length === 0) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') deselectAllOrders();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedOrders.length, deselectAllOrders]);


  const ListRenderOrders = () => {
    return (
      <div
        className="orders-list-view"
      >
        {orders.map((item, i) => {
          const providerKey = item?.antaris ? 'antaris' : 'strataTest';
          return (
            <div className="order-list-item bottomBorder" key={i}>
              <div className="priority-icon">
                <img src={getPrioritySrc(item)} alt="priority" />
              </div>
              <div className="item-details">
                <div className="item-title">{item.name ? item.name : `Antaris-${item.providerID.split('-')[0]}`}</div>
                <div className="item-sub-title">
                  {/* <span className="aoi-icon">
                    <img src={aoiImg} alt="aoiImg" />
                  </span>
                  <span className="aoi-label">{item?.antaris?.aoi?.label ? item?.antaris?.aoi?.label : 'AOI'}</span> */}
                  <span className="item-date">{formatFullDate(item.createdAt)}</span>
                  <span className="item-date"> | {item?.status}</span>
                  {/* {item?.items?.length > 0 && (
                    <span className="item-tag" title={`${item.items.length} image${item.items.length > 1 ? 's' : ''} available`}>
                      {item.items.length} image{item.items.length > 1 ? 's' : ''}
                    </span>
                  )} */}
                </div>
              </div>
              {(item?.[providerKey]?.aoi) && providerKey !== 'strataTest' &&
                (
                  <div className="item-content">
                    {item?.[providerKey]?.recurring &&
                      (
                        <span className="recurring-icon">
                          <img src={recurringImg} alt="recurringImg" />
                        </span>
                      )
                    }
                    <div className="item-location">Order Details</div>
                    <div className="item-tags-group">
                      <div className="item-tag">
                        <span className="item-tag-icon">
                          {/* <img src={coverageWindowImg} alt="coverageWindowImg" /> */}
                        </span>
                        {item?.[providerKey]?.resolution}
                      </div>
                      <div className="item-tag">
                        <span className="item-tag-icon">
                          <img src={cloudPercentageImg} alt="cloudPercentageImg" />
                        </span>
                        {item?.[providerKey]?.cloudCoverMax}</div>
                      <div className="item-tag">
                        <span className="item-tag-icon">
                          <img src={orderDataTypeImg} alt="orderDataTypeImg" />
                        </span>
                        {item?.[providerKey]?.imagingSpectrum}
                      </div>
                    </div>
                  </div>
                )}
              {item?.status === 'Draft' && (
                <div className="d-flex gap-xl-3">
                  <div className={`filter-btn btn-green`} onClick={() => { handleEditOrder(item, "Order Creation") }}>
                    <TickIcon />
                  </div>
                  <div className={`filter-btn btn-red`} onClick={() => { handleDeleteOrder(item) }}>
                    <DustBinIcon />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }
  const priorityLevel = (item) => {
    const lane = String(item?.antaris?.priorityLane ?? item?.priority ?? '').toLowerCase();
    if (lane === 'asap' || lane === 'high') return 'high';
    if (lane === 'medium') return 'medium';
    if (lane === 'low') return 'low';
    return 'normal';
  };

  const ChevronUp = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="4" viewBox="0 0 10 4" fill="none">
      <path d="M0.417969 3.34229L4.70996 0.48096C4.83592 0.396985 5.00002 0.396985 5.12598 0.48096L9.41797 3.34229" stroke="#F2A93B" strokeWidth="0.836073" strokeLinecap="round" />
    </svg>
  );

  const PRIORITY_META = {
    high: {
      label: 'High',
      glyph: <span className="priority-icon-stack"><ChevronUp /><ChevronUp /></span>,
    },
    medium: { label: 'Medium', glyph: <ChevronUp /> },
    low: {
      label: 'Low',
      glyph: (
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="4" viewBox="0 0 10 4" fill="none">
          <path d="M0.417969 0.417969L4.70996 3.27929C4.83592 3.36327 5.00002 3.36327 5.12598 3.27929L9.41797 0.417969" stroke="#9C9C9C" strokeWidth="0.836073" strokeLinecap="round" />
        </svg>
      ),
    },
    normal: {
      label: 'Normal',
      glyph: (
        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="4" viewBox="0 0 7 4" fill="none">
          <path d="M0.375 0.375H6.375M0.375 3.375H6.375" stroke="#9C9C9C" strokeWidth="0.75" strokeLinecap="round" />
        </svg>
      ),
    },
  };

  // Raw API status -> display group (summary cards + status badge)
  const STATUS_GROUPS = {
    success: { label: 'Success' },
    active: { label: 'Active' },
    pending: { label: 'Pending' },
    failed: { label: 'Failed' },
    rejected: { label: 'Rejected' },
  };

  // currentColor icons so the badge's status colour applies to them
  const svgProps = { width: 12, height: 12, viewBox: '0 0 14 14', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const STATUS_ICONS = {
    success: <svg {...svgProps}><path d="M2 7.5l3.2 3.2L12 3.5" /></svg>,
    active: <svg {...svgProps}><path d="M7 1.5a5.5 5.5 0 1 0 5.5 5.5" /></svg>,
    pending: <svg {...svgProps}><circle cx="7" cy="7" r="5.5" /><path d="M7 4v3l2 1.5" /></svg>,
    failed: <svg {...svgProps}><path d="M3 3l8 8M11 3l-8 8" /></svg>,
    rejected: <svg {...svgProps}><circle cx="7" cy="7" r="5.5" /><path d="M3.2 10.8l7.6-7.6" /></svg>,
  };

  const getStatusGroup = (status) => {
    switch (String(status ?? '').toLowerCase()) {
      case 'success':
        return 'success';
      case 'active':
      case 'dataready':
      case 'accepted':
        return 'active';
      case 'failed':
        return 'failed';
      case 'rejected':
        return 'rejected';
      default:
        return 'pending'; // Draft / Pending / Submitted
    }
  };

  const timeAgo = (date) => {
    const ts = date ? new Date(date).getTime() : NaN;
    if (Number.isNaN(ts)) return '';
    const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.floor(hrs / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const pad2 = (n) => String(n).padStart(2, '0');

  const RenderOrders = (order) => {
    const counts = order.reduce((acc, item) => {
      const g = getStatusGroup(item?.status);
      acc[g] = (acc[g] || 0) + 1;
      return acc;
    }, {});
    const visibleOrders = order.filter(item =>
      (!statusGroupFilter || getStatusGroup(item?.status) === statusGroupFilter) &&
      (!priorityFilter || priorityLevel(item) === priorityFilter)
    );

    return (
      <div className="orders-list-view user-select-none">
        <div className="orders-summary">
          <div className="orders-summary-title">Summary</div>
          <div className="orders-summary-cards">
            {Object.entries(STATUS_GROUPS).map(([key, g]) => (
              <div
                key={key}
                className={`orders-summary-card status-${key} ${statusGroupFilter === key ? 'active' : ''}`}
                onClick={() => setStatusGroupFilter(prev => (prev === key ? null : key))}
              >
                <div className="orders-summary-count">{pad2(counts[key] || 0)}</div>
                <div className="orders-summary-label">
                  <span className="orders-summary-dot" />
                  {g.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="orders-priority-legend">
          {Object.entries(PRIORITY_META).map(([key, p]) => (
            <span
              key={key}
              className={`orders-priority-option ${priorityFilter === key ? 'active' : ''}`}
              onClick={() => setPriorityFilter(prev => (prev === key ? null : key))}
            >
              <span className={`orders-priority-glyph priority-${key}`}>{p.glyph}</span>
              {p.label}
            </span>
          ))}
        </div>

        {visibleOrders.length === 0 && (
          <div className="orders-empty">No matching orders</div>
        )}

        {visibleOrders.map((item, i) => {
          const providerKey = item?.antaris ? 'antaris' : 'strataTest';
          const details = item?.[providerKey];
          const isSelected = selectedOrders.some(
            selected => selected.item.providerTaskID === item.providerTaskID
          );
          const prioKey = priorityLevel(item);
          const prio = PRIORITY_META[prioKey];
          const groupKey = getStatusGroup(item?.status);
          const group = STATUS_GROUPS[groupKey];
          const isDraft = item?.status === 'Draft';
          const imageCount = item?.items?.length ?? 0;
          const meta = [
            groupKey === 'success' ? `${pad2(imageCount)} Images` : null,
            details?.imagingSpectrum ? `Sensor-${details.imagingSpectrum}` : null,
            details?.cloudCoverMax != null ? `Cloud ${details.cloudCoverMax}%` : null,
          ].filter(Boolean).join(' | ');

          return (
            <div
              className={`order-list-item order-row bottomBorder ${isSelected ? "active-order-item" : ""}`}
              key={i}
              onClick={(e) => handleOrderClick(item, e)}
            >
              <div className={`order-row-priority priority-${prioKey}`}>
                {prio.glyph}
              </div>
              <div className="item-details">
                <div className="item-title">{item.name ? item.name : `Antaris-${item.providerID.split('-')[0]}`}</div>
                <div className="order-row-meta">{meta}</div>
                <div className="order-row-updated">
                  Updated {timeAgo(item.updatedAt ?? item.createdAt)}
                </div>
              </div>
              {isDraft ? (
                <div className="d-flex gap-xl-3">
                  <div className="filter-btn btn-green" onClick={(e) => {
                    e.stopPropagation();
                    handleEditOrder(item, "Order Creation");
                  }}>
                    <TickIcon />
                  </div>
                  <div className="filter-btn btn-red" onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteOrder(item);
                  }}>
                    <DustBinIcon />
                  </div>
                </div>
              ) : (
                <div
                  data-tooltip-id="statusTip"
                  data-tooltip-content={item?.status}
                  className={`order-status-badge status-${groupKey}`}
                >
                  {STATUS_ICONS[groupKey]}
                  {group.label}
                </div>
              )}
            </div>
          )
        })}
        <Tooltip id="statusTip" />
      </div>
    )
  }
  // AOI center as "28.21° N, 94.72° E". Point AOIs carry [lon, lat]; other
  // geometries fall back to the centre of their coordinate bounding box.
  const formatAoiCenter = (aoi) => {
    if (!aoi?.coordinates) return null;
    let lon;
    let lat;
    if (aoi.type === 'Point') {
      [lon, lat] = aoi.coordinates;
    } else {
      const pts = aoi.coordinates.flat(Infinity);
      const lons = pts.filter((_, i) => i % 2 === 0);
      const lats = pts.filter((_, i) => i % 2 === 1);
      lon = (Math.min(...lons) + Math.max(...lons)) / 2;
      lat = (Math.min(...lats) + Math.max(...lats)) / 2;
    }
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;
  };

  // AOI area as "8.6 km²" (or "850 m²" when small). Point AOIs have no area,
  // so this returns null and the cell is left out.
  const formatAoiSize = (aoi) => {
    if (aoi?.type !== 'Polygon' && aoi?.type !== 'MultiPolygon') return null;
    const sqMeters = turf.area({ type: 'Feature', geometry: aoi, properties: {} });
    if (!(sqMeters > 0)) return null;
    return sqMeters >= 1e6
      ? `${(sqMeters / 1e6).toFixed(2)} km²`
      : `${Math.round(sqMeters)} m²`;
  };

  // Order details card - bottom-right, single-order only. Compact by default;
  // "View More" expands it. Cells for fields the API doesn't return (AOI size,
  // image mode, collection name) are left out rather than shown empty.
  const OrderDetailsCard = (orderDetails) => {
    if (!orderDetails || !Object.keys(orderDetails).length) return null;

    const providerKey = orderDetails?.antaris ? 'antaris' : 'strataTest';
    const providerDetails = orderDetails?.[providerKey];
    const title = orderDetails.name ? orderDetails.name : `Antaris-${orderDetails.providerID?.split('-')[0]}`;
    const capturedCount = orderDetails?.items?.length ?? 0;
    // captured data is only viewable once the order has succeeded
    const isSuccess = orderDetails.status?.toLowerCase() === 'success';
    const capturedEnabled = isSuccess && capturedCount > 0;
    const statusGroup = getStatusGroup(orderDetails.status);
    const aoiCenter = formatAoiCenter(providerDetails?.aoi);
    const aoiSize = formatAoiSize(providerDetails?.aoi);
    const providerName = providersData?.find((p) => p.providerID === orderDetails.providerID)?.name;
    const collectionName = collectionsData?.find((c) => c.collectionID === orderDetails.collectionID)?.name;

    const cells = [
      { label: 'Order Created', value: formatFullDate(orderDetails.createdAt), expandedOnly: false },
      isSuccess && orderDetails.updatedAt
        ? { label: 'Order Completed', value: formatFullDate(orderDetails.updatedAt), expandedOnly: true }
        : null,
      providerName ? { label: 'Geo Data Provider', value: providerName, expandedOnly: true } : null,
      aoiSize ? { label: 'AOI size', value: aoiSize, expandedOnly: false } : null,
      aoiCenter ? { label: 'AOI center', value: aoiCenter, expandedOnly: false } : null,
      collectionName ? { label: 'Collection', value: collectionName, expandedOnly: true } : null,
    ].filter(Boolean).filter(c => orderDetailsExpanded || !c.expandedOnly);

    return (
      <div className="order-details-pannel">
        <div className="order-details-pannel-top">
          <div className="order-details-pannel-header">
            <div
              className={`order-details-pannel-heading ${orderDetailsCollapsed ? 'collapsed' : ''}`}
              onClick={() => setOrderDetailsCollapsed(prev => !prev)}
            >
              <svg className="order-details-pannel-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 10.5L8 5.5l5 5" />
              </svg>
              <span className="order-details-pannel-name" title={title}>{title}</span>
            </div>
            <div className={`order-status-badge status-${statusGroup}`}>
              {STATUS_ICONS[statusGroup]}
              {STATUS_GROUPS[statusGroup].label}
            </div>
          </div>
          {!orderDetailsCollapsed && (
            <div className="order-details-pannel-chips">
              {providerDetails?.resolution && (
                <div className="order-details-pannel-chip" title={providerDetails.resolution}>
                  <GsdIcon />
                  <span>{providerDetails.resolution}</span>
                </div>
              )}
              {providerDetails?.imagingSpectrum && (
                <div className="order-details-pannel-chip">
                  <SensorIcon />
                  <span>{providerDetails.imagingSpectrum}</span>
                </div>
              )}
              <div
                className={`order-details-pannel-chip ${capturedEnabled ? 'clickable' : ''} ${capturedEnabled && capturedDataVisible ? 'active' : ''}`}
                title={capturedEnabled ? 'Toggle captured data on map' : undefined}
                onClick={capturedEnabled ? handleToggleCapturedData : undefined}
              >
                <ImageCountIcon />
                <span>{pad2(capturedCount)}</span>
              </div>
            </div>
          )}
        </div>

        {!orderDetailsCollapsed && (
          <>
            <div className="order-details-pannel-grid">
              {cells.map(c => (
                <div className="order-details-pannel-cell" key={c.label}>
                  <div className="order-details-pannel-subtitle">{c.label}</div>
                  <div className="order-details-pannel-value">{c.value}</div>
                </div>
              ))}
            </div>

            <div className="order-details-pannel-actions">
              <button type="button" className="order-details-pannel-btn hide" onClick={deselectAllOrders}>Hide</button>
              <button type="button" className="order-details-pannel-btn primary" onClick={() => setOrderDetailsExpanded(prev => !prev)}>
                {orderDetailsExpanded ? 'View Less' : 'View More'}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <>
      {!listView && (<div className="mainView">
        <MapView />
        <div className="d-flex">
          <LeftNavigationBar />
          <div className="orderBars">
            <div id="orderBar">
              <div>
                <div className="d-flex order-title">
                  <div className="d-flex">
                    <span className="orders-icon"><OrderSvg /></span>
                    <span className="orders-title">Orders</span>
                    {selectedOrders.length > 1 && (
                      <span className="orders-title" style={{ marginLeft: '10px', fontSize: '14px' }}>
                        ({selectedOrders.length} selected)
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-xl-1">
                    <div
                      className="orders-info-icon"
                      onMouseEnter={() => setShowStatusLegend(true)}
                      onMouseLeave={() => setShowStatusLegend(false)}
                    >
                      <span><InfoIcon /></span>
                      {showStatusLegend && (
                        <div className="status-legend-box">
                          {Object.entries(statusIcon).map(([label, icon]) => (
                            <div className="status-legend-row" key={label}>
                              <span className="status-legend-icon">{icon}</span>
                              <span className="status-legend-label">{label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="header-option-subItem"><span onClick={() => handleFilterToggle()}><FilterIcon /></span>
                      {openFilter && (
                        <div className={`filterOptions-box ${listView ? '' : 'map-position'}`}>
                          <SingleSelectDropdown
                            options={propertyOptions}
                            onSelectItemChange={handleDropdownChange}
                            keyName="propertyID"
                            valueName="name"
                            selectedValue={filters.field}
                            dropdownName="field"
                            placeholder="Property"
                            toggleClass="dropdown-toggle-width"
                          />
                          <SingleSelectDropdown
                            options={equalityOptions}
                            onSelectItemChange={handleDropdownChange}
                            keyName="propertyID"
                            valueName="name"
                            selectedValue={filters.operator}
                            dropdownName="operator"
                            placeholder="is Equals"
                            toggleClass="dropdown-toggle-width"
                          />
                          <SingleSelectDropdown
                            options={getValueOptions()}
                            onSelectItemChange={handleDropdownChange}
                            keyName="propertyID"
                            valueName="name"
                            selectedValue={filters.values}
                            dropdownName="values"
                            placeholder="Values"
                            toggleClass="dropdown-toggle-width"
                          />
                          <div className={`filter-btn ${isFilterComplete ? "btn-red" : ""}`} onClick={handleRemoveFilter}>
                            <DustBinIcon />
                          </div>
                          <div className={`filter-btn ${isFilterComplete ? "btn-green" : ""}`} onClick={handleSubmitFilter}>
                            <TickIcon />
                          </div>

                        </div>
                      )}
                    </div>
                    <div className="toggle-btn" onClick={() => handleToggleListView()}><ListIcon /></div>
                    {filterList.length > 0 && <div className="clear-btn" onClick={() => clearFilter()}>Clear</div>}
                  </div>
                </div>
                <div className="d-flex gap-xl-1">
                  {filterList.length > 0 && (
                    filterList.map((item, index) => {
                      const operatorLabel = item.operator === "equals" ? "=" : "≠";
                      const handleRemove = () => {
                        setFilterList(prev => prev.filter((_, i) => i !== index));
                      };
                      return (
                        <div className="filterList-bar" key={index}>
                          <div>{item.field}</div>
                          <div>{operatorLabel}</div>
                          <div>{item.values[0]}</div>
                          <div onClick={handleRemove} style={{ cursor: "pointer" }}><Close /></div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
              {!loadingOrders &&
                <div className="order-dropdown-box">
                  <div className={`order-section`}>
                    {orders?.length > 0 && RenderOrders(orders)}
                  </div>
                </div>}
              {loadingOrders &&
                <div className="order-dropdown-box">
                  <Loader inverse center content="loading..." />
                </div>
              }
            </div>
          </div>
        </div>
        {OrderDetailsCard(singleOrder)}
      </div>)}
      {listView && (<div className="ordersView">
        <LeftNavigationBar />
        <div className="orders-section">
          <div className="orders-header bottomBorder">
            <div className="d-flex justify-content-between order-header-options">
              <div className="d-flex">
                <span className="orders-icon"><OrderSvg /></span>
                <span className="orders-title">Orders</span>
              </div>
              <div className="d-flex gap-xl-1">
                <div className="header-option-subItem">
                  <span onClick={() => handleFilterToggle()}><FilterIcon /></span>
                  {openFilter && (
                    <div className={`filterOptions-box ${listView ? 'list-position' : ''}`}>
                      <SingleSelectDropdown
                        options={propertyOptions}
                        onSelectItemChange={handleDropdownChange}
                        keyName="propertyID"
                        valueName="name"
                        selectedValue={filters.field}
                        dropdownName="field"
                        placeholder="Property"
                        toggleClass="dropdown-toggle-width"
                      />
                      <SingleSelectDropdown
                        options={equalityOptions}
                        onSelectItemChange={handleDropdownChange}
                        keyName="propertyID"
                        valueName="name"
                        selectedValue={filters.operator}
                        dropdownName="operator"
                        placeholder="is Equals"
                        toggleClass="dropdown-toggle-width"
                      />
                      <SingleSelectDropdown
                        options={getValueOptions()}
                        onSelectItemChange={handleDropdownChange}
                        keyName="propertyID"
                        valueName="name"
                        selectedValue={filters.values}
                        dropdownName="values"
                        placeholder="Values"
                        toggleClass="dropdown-toggle-width"
                      />
                      <div className={`filter-btn ${isFilterComplete ? "btn-red" : ""}`} onClick={handleRemoveFilter}>
                        <DustBinIcon />
                      </div>
                      <div className={`filter-btn ${isFilterComplete ? "btn-green" : ""}`} onClick={handleSubmitFilter}>
                        <TickIcon />
                      </div>

                    </div>
                  )}
                </div>
                <div onClick={() => handleToggleMapView()} className="toggle-btn">
                  <MapViewIcon />
                </div>
                {filterList.length > 0 && <div className="clear-btn" onClick={() => clearFilter()}>Clear</div>}
              </div>
            </div>
            <div className="d-flex gap-xl-1">
              {filterList.length > 0 && (
                filterList.map((item, index) => {
                  const operatorLabel = item.operator === "equals" ? "=" : "≠";
                  const handleRemove = () => {
                    setFilterList(prev => prev.filter((_, i) => i !== index));
                  };
                  return (
                    <div className="filterList-bar" key={index}>
                      <div>{item.field}</div>
                      <div>{operatorLabel}</div>
                      <div>{item.values[0]}</div>
                      <div onClick={handleRemove} style={{ cursor: "pointer" }}><Close /></div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
          {
            loadingOrders ? <Loader inverse center content="loading..." /> :
              orders.length > 0 ?
                ListRenderOrders() :
                <h5 style={{ marginTop: '10px' }}>No orders</h5>
          }
        </div>
      </div>)}
    </>
  );
};

export default OrdersView;